import { createAsyncThunk } from '@reduxjs/toolkit'
import axiosInstance from './axios/axiosConfig'
import { endpoints } from './axios/endpoints'
import ScheduledSendService from './scheduledSendService'
import withApiError from './axios/withApiError'

// All thunks reject with the API's own error body so callers can surface
// the real message instead of RTK's generic serialized error.

// Rows per list page (the route's `page_size`): the sizes offered, and the
// default. The service's own default of 10 made 2,211 active production rows
// about 220 pages.
export const PIPELINE_PAGE_SIZES = [25, 50, 100]
export const LIST_PAGE_SIZE = 50

// How many more reads a page may make when one comes back empty with a
// cursor still left (a search reads at most 5,000 rows a call, so a rare
// name can need several calls before its first match).
export const MAX_EMPTY_HOPS = 5

const PipelineService = {
    getPipelines: createAsyncThunk(
        'pipeline/getPipelines',
        withApiError((eventID) =>
            // The list shows its own loading row, so the full-screen loader
            // stays off (it kept covering rows that had already arrived).
            axiosInstance.get(endpoints.getPipelines(eventID), {
                background: true,
            })
        )
    ),
    /**
     * One page of the pipelines in a status (`active` or `inactive`),
     * `pageSize` at a time. With `search`, only the pipelines whose name or
     * event type contains it (the search route). `cursor` (the previous
     * page's last_evaluated_key) loads the page after it; without it, the
     * first. `page` (the page's index, 0 for the first) is for the slice.
     *
     * A page that comes back empty while a cursor remains (a search that read
     * 5,000 rows without a match) is followed by up to MAX_EMPTY_HOPS more
     * reads, so the list does not show an empty page with Next still on. The
     * answer is the last read's: its rows and its cursor.
     */
    listPipelines: createAsyncThunk(
        'pipeline/listPipelines',
        withApiError(
            async ({
                status = 'active',
                search = '',
                pageSize = LIST_PAGE_SIZE,
                cursor = null,
            } = {}) => {
                let key = cursor
                for (let hop = 0; ; hop += 1) {
                    const resp = await axiosInstance.get(
                        search
                            ? endpoints.searchPipelines
                            : endpoints.getActivePipelines,
                        {
                            params: {
                                status,
                                ...(search && { search_param: search }),
                                page_size: pageSize,
                                ...(key && { last_evaluated_key: key }),
                            },
                            // A read: the API's "Pipelines found" is not worth a toast.
                            silentToast: true,
                            background: true,
                        }
                    )
                    const page = resp?.data ?? {}
                    const next = page.last_evaluated_key ?? null
                    if ((page.pipelines ?? []).length || !next) return resp
                    if (hop >= MAX_EMPTY_HOPS) return resp
                    key = next
                }
            }
        )
    ),
    getPipelineHistory: createAsyncThunk(
        'pipeline/getPipelineHistory',
        withApiError((pipelineName) =>
            axiosInstance.get(endpoints.getPipelineHistory(pipelineName))
        )
    ),
    createPipeline: createAsyncThunk(
        'pipeline/createPipeline',
        withApiError((data) =>
            axiosInstance.post(endpoints.createPipeline, data)
        )
    ),
    // The v2 update checks that the body's `id` matches the URL.
    updatePipeline: createAsyncThunk(
        'pipeline/updatePipeline',
        withApiError((data) =>
            axiosInstance.put(endpoints.updatePipeline(data.id), data)
        )
    ),
    // One pipeline by id: lets the edit page load after a reload, when the
    // list of the selected event type is not in the store.
    getPipeline: createAsyncThunk(
        'pipeline/getPipeline',
        withApiError((pipelineID) =>
            axiosInstance.get(endpoints.getPipeline(pipelineID))
        )
    ),
    deletePipeline: createAsyncThunk(
        'pipeline/deletePipeline',
        withApiError((pipelineID) =>
            // The delete dialog shows a failure itself.
            axiosInstance.delete(endpoints.deletePipeline(pipelineID), {
                silentErrorToast: true,
            })
        )
    ),
    // Receiver types and each existing event type's receiver.
    getPipelineEnums: createAsyncThunk(
        'pipeline/getPipelineEnums',
        withApiError(() =>
            // The wizard falls back to its own list when this fails.
            axiosInstance.get(endpoints.getPipelineEnums, {
                silentToast: true,
                silentErrorToast: true,
                background: true,
            })
        )
    ),
    // The integration payload of an event type; its dialog shows any failure.
    getPipelinePayload: createAsyncThunk(
        'pipeline/getPipelinePayload',
        withApiError((eventType) =>
            axiosInstance.get(endpoints.getPipelinePayload, {
                params: { event_type: eventType },
                silentToast: true,
                silentErrorToast: true,
            })
        )
    ),
    // Future sends queued for an event type (the delete dialog checks them).
    getScheduledSends: createAsyncThunk(
        'pipeline/getScheduledSends',
        withApiError((eventType) => ScheduledSendService.list(eventType))
    ),
    // Cancel one scheduled send. Silent: one toast per send would pile up,
    // and the delete dialog reports the outcome.
    cancelScheduledSend: createAsyncThunk(
        'pipeline/cancelScheduledSend',
        withApiError((body) => ScheduledSendService.cancel(body))
    ),
    getEventTypes: createAsyncThunk(
        'pipeline/getEventTypes',
        withApiError(() =>
            axiosInstance.get(endpoints.getEventTypes, { background: true })
        )
    ),
}

export default PipelineService
